import {
  resetSelectedMedicalRecordsFilters,
  selectMedicalRecordsFilters,
  selectMedicalRecordsPagination,
  selectSelectedMedicalRecordsFilters,
  selectSelectedMedicalRecordsUploadDateRange,
  selectUserAboutData,
  setSelectedMedicalRecordsFilters,
  setSelectedMedicalRecordsUploadDateRange,
  setMedicalRecordsPagination,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import { Button, CloseIcon, RefreshIcon } from '@repo/atomic-components';
import { SearchTextControl, SearchTextControlHandle } from './SearchTextControl';
import { ChangeEvent, useCallback, useMemo, useRef } from 'react';
import { useDragAndDropFilesUpload } from '../hooks';
import { DEFAULT_HIDDEN_STATUS_LABELS, MEDICAL_RECORDS_TAG, medicalRecordsInvalidateTags } from '@/api';
import { classNames } from 'primereact/utils';

export const ActionsPanel = () => {
  const dispatch = useAppDispatch();
  const userAboutData = useAppSelector(selectUserAboutData);
  const { onFilesStartUpload } = useDragAndDropFilesUpload();

  const searchTextControlRef = useRef<SearchTextControlHandle | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const medicalRecordsFilters = useAppSelector(selectMedicalRecordsFilters);
  const medicalRecordsPagination = useAppSelector(selectMedicalRecordsPagination);
  const selectedMedicalRecordsFilters = useAppSelector(selectSelectedMedicalRecordsFilters);
  const selectedUploadDateRange = useAppSelector(selectSelectedMedicalRecordsUploadDateRange);

  const handleRefresh = useCallback(() => {
    dispatch(medicalRecordsInvalidateTags([MEDICAL_RECORDS_TAG]));
  }, [dispatch]);

  // GDPR check statuses for the quick filter
  const gdprCheckStatuses = useMemo(
    () =>
      medicalRecordsFilters.documentStatus.filter((s) =>
        ['Ready For GDPR Check', 'GDPR Check In Progress', 'Removing Breach Pages'].includes(s.rendered),
      ),
    [medicalRecordsFilters.documentStatus],
  );

  // AI-ready statuses for the quick filter
  const aiReadyStatuses = useMemo(
    () =>
      medicalRecordsFilters.documentStatus.filter((s) =>
        ['Ready For AI', 'AI Processing Complete'].includes(s.rendered),
      ),
    [medicalRecordsFilters.documentStatus],
  );

  // Check if GDPR quick filter is active
  const isGdprCheckActive = useMemo(() => {
    const selected = selectedMedicalRecordsFilters.documentStatus;
    if (selected.length !== gdprCheckStatuses.length) return false;
    const gdprSet = new Set(gdprCheckStatuses.map((s) => s.raw));
    return selected.every((s) => gdprSet.has(s.raw));
  }, [gdprCheckStatuses, selectedMedicalRecordsFilters.documentStatus]);

  // Check if Not AI-ready quick filter is active
  const isNotAiReadyActive = useMemo(() => {
    const selected = selectedMedicalRecordsFilters.documentStatus;
    const notAiReadyStatuses = medicalRecordsFilters.documentStatus.filter(
      (s) => !aiReadyStatuses.some((ai) => ai.raw === s.raw),
    );
    if (selected.length !== notAiReadyStatuses.length) return false;
    const notAiReadySet = new Set(notAiReadyStatuses.map((s) => s.raw));
    return selected.every((s) => notAiReadySet.has(s.raw));
  }, [aiReadyStatuses, medicalRecordsFilters.documentStatus, selectedMedicalRecordsFilters.documentStatus]);

  // The Case-status set the "Uploaded by me" chip pins alongside the user
  // filter — every option that isn't hidden by default (Closed / Awaiting
  // CMS Closure). Shares the DEFAULT_HIDDEN_STATUS_LABELS source with the
  // Case List page so the two screens stay in sync.
  const uploadedByMeCaseStatuses = useMemo(
    () => medicalRecordsFilters.caseStatus.filter((s) => !DEFAULT_HIDDEN_STATUS_LABELS.has(s.rendered)),
    [medicalRecordsFilters.caseStatus],
  );

  // Check if Uploaded by me quick filter is active — the user is filtered
  // to themselves AND the case-status filter matches the non-hidden set the
  // chip applies. Excluding the case-status check would keep the chip lit
  // when the user later toggled a hidden status back on, which contradicts
  // what the chip promises.
  const isUploadedByMeActive = useMemo(() => {
    if (!userAboutData) return false;
    const uploadedBy = selectedMedicalRecordsFilters.uploadedBy;
    if (uploadedBy.length !== 1 || uploadedBy[0].userId !== userAboutData.userId) return false;
    const caseStatus = selectedMedicalRecordsFilters.caseStatus;
    if (caseStatus.length !== uploadedByMeCaseStatuses.length) return false;
    const allowed = new Set(uploadedByMeCaseStatuses.map((s) => s.raw));
    return caseStatus.every((s) => allowed.has(s.raw));
  }, [
    selectedMedicalRecordsFilters.uploadedBy,
    selectedMedicalRecordsFilters.caseStatus,
    uploadedByMeCaseStatuses,
    userAboutData,
  ]);

  // Check if any filters are active
  const anyFilterActive = useMemo(
    () =>
      selectedMedicalRecordsFilters.caseStatus.length > 0 ||
      selectedMedicalRecordsFilters.checkedBy.length > 0 ||
      selectedMedicalRecordsFilters.documentStatus.length > 0 ||
      selectedMedicalRecordsFilters.providerTypes.length > 0 ||
      selectedMedicalRecordsFilters.providers.length > 0 ||
      selectedMedicalRecordsFilters.uploadedBy.length > 0 ||
      Boolean(selectedUploadDateRange.minUploadDate || selectedUploadDateRange.maxUploadDate),
    [selectedMedicalRecordsFilters, selectedUploadDateRange],
  );

  const resetPagination = useCallback(() => {
    dispatch(setMedicalRecordsPagination({ ...medicalRecordsPagination, pageIndex: 0 }));
  }, [dispatch, medicalRecordsPagination]);

  const clearAll = useCallback(() => {
    dispatch(resetSelectedMedicalRecordsFilters());
    resetPagination();
  }, [dispatch, resetPagination]);

  const applyGdprCheck = useCallback(() => {
    dispatch(
      setSelectedMedicalRecordsFilters({
        caseStatus: [],
        checkedBy: [],
        documentStatus: gdprCheckStatuses,
        providerTypes: [],
        providers: [],
        uploadedBy: [],
      }),
    );
    dispatch(setSelectedMedicalRecordsUploadDateRange({}));
    resetPagination();
  }, [dispatch, gdprCheckStatuses, resetPagination]);

  const applyNotAiReady = useCallback(() => {
    // Filter out AI-ready statuses
    const notAiReadyStatuses = medicalRecordsFilters.documentStatus.filter(
      (s) => !aiReadyStatuses.some((ai) => ai.raw === s.raw),
    );
    dispatch(
      setSelectedMedicalRecordsFilters({
        caseStatus: [],
        checkedBy: [],
        documentStatus: notAiReadyStatuses,
        providerTypes: [],
        providers: [],
        uploadedBy: [],
      }),
    );
    dispatch(setSelectedMedicalRecordsUploadDateRange({}));
    resetPagination();
  }, [dispatch, medicalRecordsFilters.documentStatus, aiReadyStatuses, resetPagination]);

  const applyUploadedByMe = useCallback(() => {
    if (!userAboutData) return;
    const me = medicalRecordsFilters.uploadedBy.find((u) => u.userId === userAboutData.userId);
    if (!me) return;
    dispatch(
      setSelectedMedicalRecordsFilters({
        caseStatus: uploadedByMeCaseStatuses,
        checkedBy: [],
        documentStatus: [],
        providerTypes: [],
        providers: [],
        uploadedBy: [me],
      }),
    );
    dispatch(setSelectedMedicalRecordsUploadDateRange({}));
    resetPagination();
  }, [dispatch, medicalRecordsFilters.uploadedBy, resetPagination, uploadedByMeCaseStatuses, userAboutData]);

  const handleUploadFilesButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      const newFiles = Array.from(selectedFiles);
      const allowedTypes: string[] = ['application/pdf'];

      const allowedFiles = newFiles.filter((file) => allowedTypes.includes(file.type));
      const invalidFiles = newFiles.filter((file) => !allowedTypes.includes(file.type));
      if (invalidFiles.length > 0) {
        console.error(
          'Not allowed files:',
          invalidFiles.map((file) => file.name),
        );
        return;
      }

      console.log(
        'Allowed files:',
        newFiles.map((file) => file.name),
      );

      onFilesStartUpload(allowedFiles);
    }
  };

  // Quick-filter chip — inactive = outlined grey pill, active = pale-mint
  // bg with dark-green border + text. `border` is always applied (even in
  // the active state) so toggling between active/inactive doesn't shift the
  // chip's box size by the border width. Clicking the active chip clears
  // all filters (toggle-off behaviour).
  const chipClass = (active: boolean) =>
    classNames(
      'flex h-8 items-center rounded-full border px-3 text-sm whitespace-nowrap transition-colors cursor-pointer',
      active
        ? 'border-fletchers-green-600 bg-fletchers-green-50 text-fletchers-green-600'
        : 'border-fletchers-gray-110 text-fletchers-black-900 hover:bg-fletchers-gray-50',
    );

  return (
    <>
      <div className="bg-fletchers-gray-25 flex h-full w-full flex-row items-center gap-2 px-6">
        <span className="text-[20px] font-semibold whitespace-nowrap">Medical Records</span>
        <Button
          outlined
          className="mr-auto flex !h-8 !w-8 shrink-0 items-center justify-center !bg-white !p-0"
          onClick={handleRefresh}
        >
          <RefreshIcon />
        </Button>

        <div className="flex items-center gap-2">
          <span className="text-fletchers-gray-200 text-sm whitespace-nowrap">Quick filters:</span>
          <button
            type="button"
            onClick={isGdprCheckActive ? clearAll : applyGdprCheck}
            className={chipClass(isGdprCheckActive)}
          >
            GDPR checks
          </button>
          <button
            type="button"
            onClick={isNotAiReadyActive ? clearAll : applyNotAiReady}
            className={chipClass(isNotAiReadyActive)}
          >
            Not AI-ready
          </button>
          <button
            type="button"
            onClick={isUploadedByMeActive ? clearAll : applyUploadedByMe}
            className={chipClass(isUploadedByMeActive)}
          >
            Uploaded by me
          </button>

          {anyFilterActive && (
            <>
              {/* 32px vertical divider separates the always-visible quick-
                  filter chips from the conditionally-visible Clear filters
                  button. Matches the right-hand divider before the search. */}
              <div className="bg-fletchers-gray-110 mx-2 h-[30px] w-px" />
              <button
                type="button"
                onClick={clearAll}
                className="text-fletchers-gray-200 hover:text-fletchers-black-900 flex h-8 cursor-pointer items-center gap-1.5 rounded-full px-0 text-sm font-semibold whitespace-nowrap"
              >
                <CloseIcon className="max-h-[10px] max-w-[10px]" />
                Clear filters
              </button>
            </>
          )}
        </div>

        {/* Vertical divider between the quick-filters group and other controls */}
        <div className="bg-fletchers-gray-110 mx-2 h-[30px] w-px" />

        <div className="w-4xs">
          <SearchTextControl ref={searchTextControlRef} />
        </div>

        <input ref={fileInputRef} type="file" hidden id="browse" onChange={handleFileChange} accept=".pdf" multiple />
        <Button
          className="shrink-0 whitespace-nowrap [&_.pi]:text-[12px] [&>span]:font-semibold"
          onClick={handleUploadFilesButtonClick}
          style={{ height: 32, paddingTop: 0, paddingBottom: 0, paddingLeft: 16, paddingRight: 16 }}
        >
          <i className="pi pi-arrow-up"></i>
          <span className="ml-2">Upload files</span>
        </Button>
      </div>
    </>
  );
};
